import React, { useCallback, useEffect, useState } from "react";
import * as Maths from "oc-commons/Maths";
import { isEmpty } from "oc-commons/Commons";
import { I18N } from "oc-I18N";
import { CircledMinus, CircledPlus } from "../inlinesvgs/CircledPlusMinus";

type Props = Partial<{
  value: number;
  /**
   * Wird aufgerufen, wenn ein neuer Wert committed wurde. Ein Commit entsteht, wenn das Eingabefeld den Focus verliert, Enter gedrückt wird, oder autoCommit ausgeführt wird.
   */
  setValue: (value: number) => void;
  setInnerValue: (value: number) => void;
  invalidCallback: (intermediateValue: number, resultingValue: number) => void;
  /** committed nach dieser Zeit Untätigkeit automatisch. 0 für kein Autocommit */
  autoCommitMs: number;
  name: string;
  id: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  isValid: (value: number) => boolean;
  unit: string;
  /** z.B. 0.01 für cm -> m Umrechnung */
  unitFactor: number;
  unitFirst?: boolean;
  /** Anazhl an Nachkommastellen */
  decimals: number;
  /** Minimaler Eingabewert */
  min: number;
  /** Maximaler Eingabewert */
  max: number;
  /** Schrittweite für die + / - Buttons, falls gesetzt, werden die Buttons angezeigt */
  step: number;
  disabled: boolean;
  hidden: boolean;
  className: string;
  label: string;
  ariaLabel: string;
  /** Fokusiert das Inputfeld */
  autoFocus: boolean;
  /** Optionale JSX-Componente, die im Addon - Div eingefügt wird */
  addon: JSX.Element;
  tabIndex: number;
  showSteps: boolean;
  /** Optionaler Callback wenn der Wert mittels Plus- oder Minus-Stepp-Button verändert wurde */
  onStepped?: (value: number) => boolean;
  innerRef: (inputElement: HTMLInputElement) => void;
  warningDiv: (internValue: string) => JSX.Element;
  /** wird als data-input-key an das input-Element gesetzt, falls vorhanden **/
  dataInputKey: string;
  roundToMinMax: boolean;
  workOnInternValue: boolean;
  type?: string;
  style: object;
}>;

export function NumberInput({
  value = 0,
  setValue = () => {},
  invalidCallback = () => {},
  autoCommitMs = 5000,
  name,
  id,
  onFocus = (value) => {},
  onBlur = (value) => {},
  isValid = (value) => true,
  unit,
  unitFactor = 1,
  unitFirst = false,
  decimals = 0,
  min,
  max,
  step,
  disabled,
  hidden,
  className,
  label,
  ariaLabel,
  autoFocus = false,
  addon,
  tabIndex,
  showSteps = true,
  onStepped = (value) => false,
  innerRef,
  warningDiv = (value) => null,
  dataInputKey,
  roundToMinMax = false,
  workOnInternValue = false,
  type = "text",
  style,
}: Props) {
  const formatFloatValue = (value: number) => {
    const v = (Maths.notFinite(value) ? 0 : value) * unitFactor;
    if (type == "text") {
      return I18N.formatFloat(v, decimals);
    } else {
      return v.toFixed(decimals);
    }
  };

  const parseFloatValue = (text: string) => {
    // max * unitFactor gibt den maximalen Wert in UI-Einheiten an
    // z.B. wenn max=10 (Meter) und unitFactor=100 (cm->m), dann ist maxInUiUnits=1000
    const maxInUiUnits = max != null ? max * unitFactor : undefined;
    return I18N.parseFloat(text, maxInUiUnits) / unitFactor;
  };

  // initialValue dient zum Vergleich, ob sich der Wert des Properties geändert hat, also ob
  // der Wert sich unabhängig von der Benutzereingabe im Arrangement geändert hat
  const [initialValue, setInitialValue] = useState<string>(formatFloatValue(value));

  // internValue ist das, was der Benutzer gerade editiert, und kann auch ein nicht valider
  // Zwischenzustand sein. Das Sollformat von internValue ist das lokale Zahlenformat, also
  // z.B. 1.234,99 für de
  const [internValue, setInternValue] = useState<string>(formatFloatValue(value));

  const setInputElementRef = useCallback((inputElementResolved: HTMLInputElement) => {
    const inputElement = inputElementResolved;

    if (innerRef) {
      innerRef(inputElement);
    }
    if (autoFocus && inputElement) {
      _.defer(() => focusOn(inputElement));
    }
  }, []);

  useEffect(() => {
    let delayedDebouncedFnTimeOutId: NodeJS.Timeout | null = null;
    const formattedValue = formatFloatValue(value);
    if (initialValue != formattedValue) {
      // falls es eine interne Datenänderung im Arrangement gegeben hat => übernimm diese
      setInternValue(formattedValue);
      setInitialValue(formattedValue);
    } else {
      // falls der Benutzer den Wert geändert, aber noch nicht committed hat => committe diesen
      if (autoCommitMs > 0) {
        if (formattedValue !== internValue) {
          setInternValue(formattedValue);
          delayedDebouncedFnTimeOutId = setTimeout(() => commitValue(formattedValue), autoCommitMs);
        }
      }
    }
    return () => {
      if (delayedDebouncedFnTimeOutId) clearTimeout(delayedDebouncedFnTimeOutId);
    };
  }, [value, initialValue]);

  const focusOn = (element: HTMLInputElement) => {
    element.focus();
    element.tagName.toLowerCase() === "input" && element.select();
  };

  const round = (value: number) => {
    if (min >= 0 && max < 10) {
      // falls die eingebbare Zahl nur einstellig ist (mit evtl. Nachkommastellen), dann werden
      // kleinere oder größere Zahlen automatisch auf eine einstellige Zahl abgebildet.
      // Beispiel: Der User gibt für die Zaunfeld-Höhe 120 ein (und meint damit 120 cm). Das
      // bilden wir dann auf 1.2 (und meinen damit m) ab.
      const log10 = Math.floor(Math.log10(value));
      if (log10 > 0) {
        value /= Math.pow(10, log10);
      }
    }
    if (decimals >= 0) {
      const p = Math.pow(10, decimals) * unitFactor;
      value = Math.round(value * p) / p;
    }
    if (Maths.notFinite(value)) {
      value = 0;
    }
    if (min != null) {
      value = Math.max(value, min);
    }
    if (max != null) {
      value = Math.min(value, max);
    }
    return value;
  };

  const betweenMinMax = (value: number) =>
    (min == null || min <= value) && (max == null || value <= max);

  const isValidAndMinMax = (value: number) => isValid(value) && betweenMinMax(value);

  const commitValue = (valueString: string) => {
    const intermediateValue = parseFloatValue(valueString);
    const valid = isValidAndMinMax(intermediateValue);
    let validValue = round(valid ? intermediateValue : value);
    if (valid) {
      setValue(validValue);
    } else {
      invalidCallback(intermediateValue, value);
      if (roundToMinMax && !isNaN(intermediateValue)) {
        validValue = Maths.cutBetween(min, intermediateValue, max);
        setValue(validValue);
      }
    }
    setInternValue(formatFloatValue(validValue));
  };

  const getStepCallback = (stepFactor: number) => () => {
    if (disabled) {
      return;
    }
    const newValue = (workOnInternValue ? parseFloatValue(internValue) : value) + stepFactor * step;
    const valid = isValidAndMinMax(newValue);
    // damit der Benutzer ein kurzes Feedback von der Eingabe erhält
    setInternValue(formatFloatValue(newValue));
    if (valid) {
      onStepped(newValue);
      commitValue(formatFloatValue(newValue));
    } else if (autoCommitMs > 0) {
      setTimeout(() => commitValue(formatFloatValue(newValue)), autoCommitMs / 2);
    }
  };

  const classNameGroup = `input-group ${className || ""} ${hidden ? "disappear" : "appear"}`;
  const classNameInput = `form-control ui-number-input:number-input ${
    isValidAndMinMax(value) ? "" : "invalid"
  }`;
  const classNameStep = `input-group-addon number-ui step ${disabled ? "disabled" : ""}`;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLInputElement;
    if (e.key === "Enter") {
      commitValue(target.value);
      target.blur();
    }
    if (e.key === "Escape") {
      target.value = formatFloatValue(value);
      target.blur();
    }
  };

  return (
    <div className="input-label-group" style={style}>
      {label && <label htmlFor={name}>{label}</label>}
      <div className={classNameGroup}>
        {addon && <span className="input-group-addon">{addon}</span>}

        {unit != null && unitFirst && <span className="unit">{unit}</span>}

        <input
          ref={setInputElementRef}
          id={id}
          name={name}
          className={classNameInput}
          disabled={disabled}
          type={type}
          value={internValue}
          step={step !== undefined ? "" + step : undefined}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInternValue(e.target.value)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            commitValue(e.target.value);
            onBlur(e);
          }}
          tabIndex={tabIndex}
          data-input-key={dataInputKey}
          aria-label={ariaLabel}
        />

        {unit != null && !unitFirst && <span className="unit">{unit}</span>}

        {step != null && !disabled && showSteps && (
          <>
            <span className={classNameStep + " minus"} onClick={getStepCallback(-1)}>
              <CircledMinus />
            </span>
            <span className={classNameStep + " plus"} onClick={getStepCallback(+1)}>
              <CircledPlus />
            </span>
          </>
        )}
      </div>

      {warningDiv(internValue)}
    </div>
  );
}
